// React
import { useState, useEffect, useContext } from 'react';
import { Route, useNavigate, Routes } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
// Microsoft
import { app, monetization } from '@microsoft/teams-js';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
// Utils
import { callFunction, loadAppsData } from '../../lib/Helpers';
import { v4 as uuidv4 } from 'uuid';
// Apps
import Subscriptions from '../../settingsPages/Subscriptions';
import Users from '../../settingsPages/Users';
import { Help } from '../../settingsPages/Help';
// Components
import { LeftMenu } from '../../widgets/LeftMenu';
import { BasePage } from '../../widgets/BasePage';
import SSO from '../../widgets/SSO';
import { Alert } from '@fluentui/react-components/unstable';
import { AppObject } from '../../widgets/AppCard';
// Styling
import { useBoolean } from '@fluentui/react-hooks';
import { useTheme } from '@fluentui/react';
import { EntObject } from '../../pages/MyHub';
import { SettingsMenuContext } from '../../contexts/SettingsMenuContext';
import { SettingsContainer, SettingsContent } from './styles';
import settingsPages from '../../json/v2/settingsmenu.json';
import { XCircle } from '@phosphor-icons/react';
import { TrialAlert } from '../../widgets/TrialAlert';
import { Checkbox } from '@fluentui/react-components';
import { TeamsFxContext } from '../../Context';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { TutorialsContext } from '../../contexts/TutorialsContext';
import { LicencePopup } from '../../widgets/LicencePopup';

interface PageProps {
  context: app.Context | undefined;
}

export const Settings = ({ context }: PageProps) => {
  const userId = context?.user?.id ?? '';
  const tenantId = context?.user?.tenant?.id ?? '';

  const { teamsUserCredential, locale } = useContext(TeamsFxContext);

  const appInsights = useAppInsightsContext();

  const { t } = useTranslation(['settings', 'alerts']);

  const [isSSO, setIsSSO] = useState<boolean>(false);
  const [exception, setException] = useState<string>('');

  const [userEntitlements, setUserEntitlements] = useState<EntObject>();
  const [allApps, setAllApps] = useState<AppObject[]>();
  const [userApps, setUserApps] = useState<AppObject[]>();

  const [isTrial, { setTrue: inTrial, setFalse: notInTrial }] =
    useBoolean(false);

  const [refreshLoading, setRefreshLoading] = useState(false);

  const [tutorialsDisabled, setTutorialsDisabled] = useState<string[]>([]);

  const { handleLinks } = useContext(SettingsMenuContext);

  const [isLicencePopupVisible, setIsLicencePopupVisible] =
    useState<boolean>(false);
  const [licencePopupType, setLicencePopupType] = useState<
    'TRIAL' | 'HAS_TRIED'
  >();
  const [hasShownLicencePopup, setHasShownLicencePopup] =
    useState<boolean>(false);

  const theme = useTheme();

  useEffect(() => {
    app.notifySuccess();
  }, []);

  useEffect(() => {
    if (userId) {
      loadAppsData(
        userId,
        setAllApps,
        setUserEntitlements,
        tenantId as string,
        teamsUserCredential
      ).catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'Settings', location: 'onLoadAppsData' },
          });
      });
    }
  }, [appInsights, teamsUserCredential, tenantId, userId]);

  useEffect(() => {
    if (userEntitlements) {
      if (!hasShownLicencePopup && userEntitlements.subscriptions.length <= 0) {
        userEntitlements.hasTried
          ? setLicencePopupType('HAS_TRIED')
          : setLicencePopupType('TRIAL');
        setIsLicencePopupVisible(true);
        setHasShownLicencePopup(true);
      }

      setUserApps(
        allApps?.filter((app) => userEntitlements.apps.includes(app.id))
      );

      setTutorialsDisabled(userEntitlements.tutorials);
    }
  }, [allApps, inTrial, userEntitlements]);

  const openPurchaseExperience = () => {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({ name: 'Upgrade from Settings clicked' });
      if (monetization.isSupported()) {
        monetization
          .openPurchaseExperience()
          .catch(() =>
            window.open(
              encodeURI(
                'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
              )
            )
          );
      } else {
        window.open(
          encodeURI(
            'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
          )
        );
      }
    }
  };

  const initiateTrialExperience = () => {
    callFunction(
      true,
      'putTrialSubForUser?{0}&tenantId=' + tenantId,
      undefined,
      false,
      teamsUserCredential
    )
      .then(() => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackEvent({
            name: 'Trial initiated from Settings clicked',
          });
        setActiveLink('home');
        navigate('home');
        window.location.reload();
      })
      .catch(() => {
        toast.error(t('TRIAL_INITIATE_ERROR', { ns: 'alerts' }));
      });
  };

  const [activeLink, setActiveLink] = useState('home');
  const navigate = useNavigate();

  const navigateToMenu = (key: string) => {
    setActiveLink(key);
    navigate(key);
    appInsights.trackEvent({
      name: 'Left Menu Clicked',
      properties: { menuItem: key },
    });
  };

  const [usersKey, setRefreshUsers] = useState('initial');
  const [subssKey, setRefreshSubs] = useState('initial');

  const onReloadRequest = (): boolean => {
    if (userId) {
      try {
        setRefreshLoading(true);
        const appsPromise = callFunction(false, 'getAllApps?{0}').then((res) =>
          setAllApps(res)
        );

        const entitlementsPromise = callFunction(
          true,
          'getUserApps?{0}&tenantId=' + tenantId,
          undefined,
          false,
          teamsUserCredential
        ).then((res) => setUserEntitlements(res));

        Promise.all([appsPromise, entitlementsPromise]).then(() => {
          setRefreshUsers(uuidv4());
          setRefreshSubs(uuidv4());
          setRefreshLoading(false);
        });
      } catch (error: any) {
        setRefreshLoading(false);

        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'Settings', location: 'onLoadAppsData' },
          });
      }

      return true;
    } else return false;
  };

  useEffect(() => {
    if (settingsPages && userApps) handleLinks(settingsPages, userApps);
  }, [handleLinks, userApps]);

  return (
    <>
      {isTrial && <TrialAlert />}
      {!context && (
        <Alert intent="warning" appearance="inverted">
          {t('NOT_IN_TEAMS', { ns: 'alerts' })}
        </Alert>
      )}
      {exception !== '' && (
        <Alert
          intent="error"
          action={{
            icon: <XCircle color="#fd6262" />,
            onClick: () => setException(''),
          }}
        >
          {t('APP_ERROR', { ns: 'alerts' })}
        </Alert>
      )}

      {!isMobile ? (
        <SettingsContainer>
          <LeftMenu
            isTrial={isTrial}
            type="settings"
            onLinkClick={navigateToMenu}
          />
          <SettingsContent isUsersPage={activeLink === 'users'}>
            <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
            {isSSO && activeLink === 'home' && (
              <BasePage text={t('BASE_PAGE_TEXT')} />
            )}
            <Routes>
              {allApps && userEntitlements && (
                <>
                  <Route
                    path="/subscriptions"
                    element={
                      <Subscriptions
                        key={subssKey}
                        appsObj={allApps}
                        entObj={userEntitlements}
                        currentUser={userId as string}
                        tid={context?.user?.tenant?.id as string}
                        theme={theme}
                        themeName={context?.app.theme}
                        onReloadRequest={onReloadRequest}
                        onUpgrade={openPurchaseExperience}
                        onTrial={initiateTrialExperience}
                        hasTried={userEntitlements.hasTried}
                        tutorials={tutorialsDisabled}
                      />
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <Users
                        key={usersKey}
                        appsObj={allApps}
                        entObj={userEntitlements}
                        currentUser={userId as string}
                        tid={context?.user?.tenant?.id as string}
                        theme={theme}
                        themeName={context?.app.theme}
                        onReloadRequest={onReloadRequest}
                        refreshLoading={refreshLoading}
                        tutorials={tutorialsDisabled}
                      />
                    }
                  />
                </>
              )}
              <Route
                path="/sso"
                element={
                  <SSO
                    isSSO={isSSO}
                    setIsSSO={setIsSSO}
                    showVisuals={true}
                    theme={theme}
                    themeName={context?.app.theme}
                  />
                }
              />
              <Route path="/help" element={<Help />} />
              <Route
                path="/empty"
                element={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1rem',
                    }}
                  >
                    <h1>{t('APP_CONFIGURATIONS_TITLES')}</h1>
                    <Checkbox
                      defaultChecked
                      label={t('APP_CONFIGURATIONS_CHECKBOX')}
                    />
                  </div>
                }
              />
            </Routes>
            {licencePopupType && (
              <LicencePopup
                isVisible={isLicencePopupVisible}
                setIsVisible={setIsLicencePopupVisible}
                tid={tenantId}
                type={licencePopupType}
              />
            )}
          </SettingsContent>
        </SettingsContainer>
      ) : (
        <SettingsContainer>
          <SettingsContent>
            <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
            {isSSO && <Help />}
          </SettingsContent>
        </SettingsContainer>
      )}
    </>
  );
};
